import { FIELD_WIDTH_FEET, LINE_OF_SCRIMMAGE, SIDELINE_BUFFER_FEET } from '@/constants'

// Derived grid snapping constants
export const MIN_X_FEET = SIDELINE_BUFFER_FEET // 2 feet (player radius)
export const MAX_X_FEET = FIELD_WIDTH_FEET - SIDELINE_BUFFER_FEET // 158 feet
export const LOS_EXCLUSION_MIN = 29
export const LOS_EXCLUSION_MAX = 31
export const LOS_Y = LINE_OF_SCRIMMAGE // 30 feet

/**
 * Snaps a position to the nearest valid grid point.
 * - Rounds to nearest integer
 * - Clamps X to [2, 158] for sideline buffers
 * - Excludes Y values of 29, 30, 31 (LOS zone), snapping to 28 or 32
 * @example
 * snapToGrid(5.7, 29.3) // { x: 6, y: 28 }
 * snapToGrid(0, 30)     // { x: 2, y: 32 }
 */
export function snapToGrid(x: number, y: number): { x: number; y: number } {
	// Guard against non-finite values (NaN, Infinity)
	if (!Number.isFinite(x) || !Number.isFinite(y)) {
		// Default to center field, offense side
		return { x: 80, y: 28 }
	}

	// Round to nearest integer
	let snappedX = Math.round(x)
	let snappedY = Math.round(y)

	// Clamp X to sideline bounds
	snappedX = Math.max(MIN_X_FEET, Math.min(MAX_X_FEET, snappedX))

	// Handle LOS exclusion zone
	if (snappedY >= LOS_EXCLUSION_MIN && snappedY <= LOS_EXCLUSION_MAX) {
		// If original Y < 30, snap to offense side (28)
		// Otherwise snap to defense side (32)
		snappedY = y < LOS_Y ? 28 : 32
	}

	return { x: snappedX, y: snappedY }
}

/**
 * Enforces field boundaries and LOS exclusion without integer rounding.
 * Use during drag for smooth movement that still respects the LOS zone.
 * Players crossing the LOS are pushed to the nearest valid side (28 or 32).
 */
export function constrainToField(x: number, y: number): { x: number; y: number } {
	if (!Number.isFinite(x) || !Number.isFinite(y)) {
		return { x: 80, y: 28 }
	}

	const clampedX = Math.max(MIN_X_FEET, Math.min(MAX_X_FEET, x))

	let clampedY = y
	if (y >= LOS_EXCLUSION_MIN && y <= LOS_EXCLUSION_MAX) {
		clampedY = y < LOS_Y ? LOS_EXCLUSION_MIN - 1 : LOS_EXCLUSION_MAX + 1
	}

	return { x: clampedX, y: clampedY }
}

/**
 * Checks if a position is a valid grid position.
 * - Must be integer coordinates
 * - X must be within [2, 158]
 * - Y must not be 29, 30, or 31 (LOS exclusion zone)
 */
export function isValidGridPosition(x: number, y: number): boolean {
	// Must be integers
	if (!Number.isInteger(x) || !Number.isInteger(y)) {
		return false
	}

	// X must be within bounds
	if (x < MIN_X_FEET || x > MAX_X_FEET) {
		return false
	}

	// Y must not be in LOS exclusion zone
	return !(y >= LOS_EXCLUSION_MIN && y <= LOS_EXCLUSION_MAX)


}
